/**
 * usePoliceDepartmentsTableColumns - Column definitions for police departments table
 */

import { ColumnDef } from '@tanstack/react-table';
import * as React from 'react';

import { ReadPoliceDepartmentWithStats } from '@/services/api/generated/policeDepartmentsService';

import { formatRelativeDate } from '../utils';
import { SortableHeader } from './SortableHeader';
import { CapabilitiesCell, ReportsStatsCell } from './table-cells';

export const usePoliceDepartmentsTableColumns = (
  ordering: string,
  onOrderingChange: (ordering: string) => void,
) => {
  return React.useMemo<ColumnDef<ReadPoliceDepartmentWithStats>[]>(
    () => [
      {
        accessorKey: 'name',
        header: () => (
          <SortableHeader
            label="Department"
            sortKey="name"
            currentOrdering={ordering}
            onOrderingChange={onOrderingChange}
          />
        ),
        size: 280,
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-gray-900">
              {row.original.name || 'Unnamed Department'}
            </div>
            {row.original.jurisdiction_type && (
              <div className="mt-0.5 text-xs capitalize text-gray-500">
                {row.original.jurisdiction_type.replace('_', ' ')}
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'state',
        header: () => (
          <SortableHeader
            label="Location"
            sortKey="state_code"
            currentOrdering={ordering}
            onOrderingChange={onOrderingChange}
          />
        ),
        size: 120,
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-medium text-gray-700">
              {row.original.state_code || '—'}
            </div>
            {row.original.state_name && (
              <div className="mt-0.5 text-xs text-gray-500">
                {row.original.state_name}
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'active_matters',
        header: () => (
          <SortableHeader
            label="Active Matters"
            sortKey="active_matters_count"
            currentOrdering={ordering}
            onOrderingChange={onOrderingChange}
          />
        ),
        size: 130,
        cell: ({ row }) => (
          <ReportsStatsCell
            activeMatters={row.original.stats?.active_matters_count || 0}
          />
        ),
      },
      {
        id: 'capabilities',
        header: () => (
          <span className="text-xs font-medium uppercase tracking-wider text-gray-500">
            Contact Methods
          </span>
        ),
        size: 140,
        cell: ({ row }) => (
          <CapabilitiesCell
            hasPortal={row.original.stats?.has_portal || false}
            hasMail={row.original.stats?.has_mail_contact || false}
            hasPhone={row.original.stats?.has_phone_contact || false}
          />
        ),
      },
      {
        accessorKey: 'modified_at',
        header: () => (
          <SortableHeader
            label="Updated"
            sortKey="modified_at"
            currentOrdering={ordering}
            onOrderingChange={onOrderingChange}
          />
        ),
        size: 100,
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {row.original.modified_at
              ? formatRelativeDate(row.original.modified_at)
              : '—'}
          </span>
        ),
      },
    ],
    [ordering, onOrderingChange],
  );
};
